import React, { FC, MouseEvent, useCallback } from 'react';
import { useFocusRing } from 'react-aria';
import { DropdownGroupTitle, DropdownItem } from '@saucelabs/ds-dropdown';
import classnames from 'classnames';

import Styles from './SkipMenu.styles';

export enum SKIP_MENU_IDS {
  CONTENT= 'app-content',
  HEADER = 'header-nav',
  SIDEBAR = 'sidebar-nav',
}

/**
 * Provides a 'skip menu' for jumping to various important sections of the app without having to
 * tab through all focusable items to get to each.
 */
export const SkipMenu: FC = () => {
  const { focusProps, isFocused } = useFocusRing({
    within: true,
  });

  const clickCallback = useCallback((event: MouseEvent) => {
    const { target } = event;

    if (target instanceof HTMLAnchorElement) {
      const href = target.getAttribute('href');
      if (href && href.indexOf('#') === 0) {
        event.preventDefault();
        document.querySelector<HTMLElement>(href)?.focus();
      }
    }
  }, []);

  return (
    <div
      className={classnames(
        Styles['skip-menu'],
        'transition-transform absolute left-1 top-1',
        'rounded border border-solid border-gray-200 shadow-md bg-white mb-0.5', {
          [Styles['skip-menu-hidden']]: !isFocused,
          [Styles['skip-menu-visible']]: isFocused,
        })}
      id="a11y-skip-menu"
    >
      <DropdownGroupTitle
        aria-hidden
        id="skip-list-title"
        role="presentation"
      >
        Skip to
      </DropdownGroupTitle>
      <ol
        aria-labelledby="skip-list-title"
        className="m-0 p-0"
        {...focusProps}
      >
        <DropdownItem
          component="a"
          href={`#${SKIP_MENU_IDS.HEADER}`}
          onClick={clickCallback}
        >
          Skip to Header Navigation
        </DropdownItem>
        <DropdownItem
          component="a"
          href={`#${SKIP_MENU_IDS.SIDEBAR}`}
          onClick={clickCallback}
        >
          Skip to Sidebar Navigation
        </DropdownItem>
        <DropdownItem
          component="a"
          href={`#${SKIP_MENU_IDS.CONTENT}`}
          onClick={clickCallback}
        >
          Skip to Application Content
        </DropdownItem>
      </ol>
    </div>
  );
};
